import { useEffect, useMemo, useState } from "react";
import {
  FormAlert,
  FormField,
  FormGrid,
  FormSection,
  TableKit,
} from "@NP-Webber/crm-kit";
import { shellApi, type FieldPolicyDto } from "../../api/shellApi";
import { toClientApiError } from "../../api/httpClient";
import { SYSTEM_ROLE_OPTIONS } from "./roleOptions";

const CUSTOMER_FIELDS = [
  { key: "name", label: "שם" },
  { key: "status", label: "סטטוס" },
  { key: "phone", label: "טלפון" },
  { key: "email", label: "אימייל" },
  { key: "address", label: "כתובת" },
  { key: "regulatoryId", label: "מזהה רגולטורי" },
] as const;

const ACCESS_OPTIONS = [
  { value: "view", label: "צפייה" },
  { value: "edit", label: "עריכה" },
  { value: "hide", label: "הסתרה" },
] as const;

const ACCESS_LABELS: Record<string, string> = {
  view: "צפייה",
  edit: "עריכה",
  hide: "הסתרה",
};

export function FieldPoliciesPanel() {
  const [roleKey, setRoleKey] = useState("general_user");
  const [items, setItems] = useState<FieldPolicyDto[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await shellApi.listFieldPolicies("customers"));
    } catch (e) {
      setError(toClientApiError(e).message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  const accessFor = (fieldKey: string) =>
    items.find((i) => i.roleKey === roleKey && i.fieldKey === fieldKey)?.access ??
    "view";

  const columns = useMemo(
    () => [
      {
        key: "label",
        title: "שדה",
        sortable: true,
        filterable: true,
        minWidth: 140,
      },
      {
        key: "fieldKey",
        title: "מפתח",
        filterable: true,
        minWidth: 120,
      },
      {
        key: "accessLabel",
        title: "גישה נוכחית",
        filterable: true,
        minWidth: 110,
      },
      {
        key: "access",
        title: "שינוי",
        minWidth: 160,
        render: (_value: unknown, row: Record<string, unknown>) => {
          const fieldKey = String(row.fieldKey ?? "");
          return (
            <FormField
              label=""
              type="select"
              value={String(row.access ?? "view")}
              disabled={savingField === fieldKey}
              onChange={(value) => {
                const access = value as "view" | "edit" | "hide";
                setSavingField(fieldKey);
                setMessage(null);
                setError(null);
                void shellApi
                  .setFieldPolicy("customers", { roleKey, fieldKey, access })
                  .then(() => {
                    setMessage("נשמר");
                    return reload();
                  })
                  .catch((e) => {
                    setError(toClientApiError(e).message);
                  })
                  .finally(() => setSavingField(null));
              }}
              options={ACCESS_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
          );
        },
      },
    ],
    [roleKey, savingField],
  );

  const rows = CUSTOMER_FIELDS.map((field) => {
    const access = accessFor(field.key);
    return {
      id: field.key,
      fieldKey: field.key,
      label: field.label,
      access,
      accessLabel: ACCESS_LABELS[access] ?? access,
    };
  });

  return (
    <section className="page-section page-section--list" aria-label="הרשאות עמודות ושדות">
      {message ? <FormAlert severity="success">{message}</FormAlert> : null}
      {error ? <FormAlert severity="error">{error}</FormAlert> : null}

      <FormSection>
        <FormGrid cols={2}>
          <FormField
            label="תפקיד"
            type="select"
            value={roleKey}
            onChange={setRoleKey}
            options={SYSTEM_ROLE_OPTIONS.map((r) => ({
              value: r.key,
              label: r.name,
            }))}
          />
        </FormGrid>
      </FormSection>

      <div className="page-section__table-surface">
        <TableKit
          columns={columns}
          data={rows as unknown as Array<Record<string, unknown>>}
          loading={loading}
          clientSideMode
          emptyMessage="אין שדות"
          showFilters
          showColumnPicker
        />
      </div>
    </section>
  );
}
